import React, { useMemo } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  MenuItem,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  CircularProgress,
  Pagination,
} from "@mui/material";

import {
  useGetShortcodesQuery,
  useGetKeywordsByShortcodeQuery,
  useGetSubkeywordsByKeywordQuery,
  useGetShortcodeReportQuery,
  useGetShortcodeSubKeywordsQuery,
  useLazyDownloadCallLogsQuery,
} from "../store/services/missedCallApi";
import dayjs from "dayjs";

/* 🎨 Styles */
const inputSx = {
  minWidth: 140,
  "& .MuiOutlinedInput-root": {
    fontSize: "0.75rem",
    background: "rgba(255,255,255,0.7)",
    "& fieldset": { borderColor: "#E2E8F0" },
    "&:hover fieldset": { borderColor: "#94A3B8" },
  },
  "& .MuiInputLabel-root": {
    fontSize: "0.75rem",
    color: "#475569",
  },
  "& .Mui-disabled": {
    "& .MuiOutlinedInput-input": {
      color: "#334155 !important",
      WebkitTextFillColor: "#334155 !important",
    },
    "& .MuiOutlinedInput-notchedOutline": { borderColor: "#CBD5E1" },
  },
};

const KeywordReport = () => {
  const today = new Date().toISOString().split("T")[0];

  // ✅ State
  const [filters, setFilters] = React.useState({
    start_date: today,
    end_date: today,
    shortcode: "",
    keyword_id: "", // keyword's id
    keyword_text: "",
    subkeyword: "",
    mobile: "",
    page: 1,
    limit: 20,
  });

  /** ============================
   *  LOAD DROPDOWNS
   * ============================ */
  // Get list of shortcodes
  const { data: shortcodeData } = useGetShortcodesQuery();

  // Get keywords for selected shortcode (pass shortcode string)
  const { data: keywordData } = useGetKeywordsByShortcodeQuery(
    filters.shortcode,
    { skip: !filters.shortcode }
  );

  const [downloadCallLogs, { isFetching: isDownloading }] =
    useLazyDownloadCallLogsQuery();

  const { data: subkeywordData } = useGetShortcodeSubKeywordsQuery(
    {
      keywordId: filters.keyword_id,
      shortcode: filters.shortcode || "",
    },
    { skip: !filters.keyword_id }
  );

  /** ============================
   *  BUILD API FILTERS & LOAD REPORT
   * ============================ */
  const apiFilters = useMemo(
    () => ({
      start_date: filters.start_date,
      end_date: filters.end_date,
      shortcode: filters.shortcode || "",
      keyword: filters.keyword_id || "",
      subkeyword: filters.subkeyword || "",
      mobile: filters.mobile || "",
      page: filters.page,
      limit: filters.limit,
    }),
    [filters]
  );

  const { data, isLoading, refetch } = useGetShortcodeReportQuery(apiFilters);
  const logs = data?.data || [];
  const totalPages = data?.totalPages || 1;

  /** ============================
   *  HANDLE INPUTS
   * ============================ */
  const handleInput = (e) => {
    const { name, value } = e.target;

    if (name === "shortcode") {
      setFilters((prev) => ({
        ...prev,
        shortcode: value,
        keyword_id: "",
        keyword_text: "",
        subkeyword: "",
        page: 1,
      }));
      return;
    }

    if (name === "keyword_id") {
      const selected = keywordData?.data?.find(
        (k) => String(k.keyword_id) === String(value)
      );
      setFilters((prev) => ({
        ...prev,
        keyword_id: value,
        keyword_text: selected?.keyword || "",
        subkeyword: "",
        page: 1,
      }));
      return;
    }

    setFilters((prev) => ({
      ...prev,
      [name]: value,
      page: 1,
    }));
  };

  const handleDownload = async () => {
    try {
      const response = await downloadCallLogs(apiFilters).unwrap();
      const blob = new Blob([response], { type: "text/csv" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        `shortcode_report_${filters.start_date}_to_${filters.end_date}.csv`
      );
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Download failed:", err);
    }
  };

  return (
    <Box sx={{ p: 2 }}>
      <Typography
        variant="h6"
        sx={{ mb: 2, fontWeight: "bold", fontSize: "1rem" }}
      >
        Keyword Report
      </Typography>

      {/* SEARCH FILTERS */}
      <Box
        sx={{
          display: "flex",
          gap: 1,
          flexWrap: "wrap",
          alignItems: "center",
          borderRadius: "0.5rem",
          padding: "1rem",
          backgroundColor: "#ffffff52",
          backdropFilter: "blur(10px)",
          boxShadow: "0 4px 12px rgba(14,165,233,0.1)",
          mb: 2,
        }}
      >
        <TextField
          label="Start Date"
          type="date"
          name="start_date"
          value={filters.start_date}
          onChange={handleInput}
          size="small"
          InputLabelProps={{ shrink: true }}
          inputProps={{ max: today }}
          sx={inputSx}
        />
        <TextField
          label="End Date"
          type="date"
          name="end_date"
          value={filters.end_date}
          onChange={handleInput}
          size="small"
          InputLabelProps={{ shrink: true }}
          inputProps={{ max: today }}
          sx={inputSx}
        />
        <TextField
          label="Short Code"
          name="shortcode"
          select
          value={filters.shortcode}
          onChange={handleInput}
          size="small"
          sx={inputSx}
        >
          <MenuItem value="">All</MenuItem>
          {shortcodeData?.data?.length > 0 ? (
            shortcodeData.data.map((item) => (
              <MenuItem key={item.id} value={item.shortcode}>
                {item.shortcode}
              </MenuItem>
            ))
          ) : (
            <MenuItem disabled>No Shortcodes</MenuItem>
          )}
        </TextField>
        <TextField
          label="Keyword"
          name="keyword_id"
          select
          value={filters.keyword_id}
          onChange={handleInput}
          size="small"
          disabled={!filters.shortcode}
          sx={inputSx}
        >
          <MenuItem value="">All</MenuItem>
          {keywordData?.data?.length > 0 ? (
            keywordData.data.map((item) => (
              <MenuItem key={item.keyword_id} value={item.keyword_id}>
                {item.keyword}
              </MenuItem>
            ))
          ) : (
            <MenuItem disabled>
              {filters.shortcode ? "No Keywords" : "Select shortcode first"}
            </MenuItem>
          )}
        </TextField>
        <TextField
          label="Mobile Number"
          name="mobile"
          placeholder="Enter mobile"
          value={filters.mobile}
          onChange={handleInput}
          size="small"
          sx={inputSx}
        />
        <Button
          variant="contained"
          sx={{
            height: 32,
            fontSize: "0.75rem",
            textTransform: "none",
            padding: "4px 10px",
          }}
          onClick={() => refetch()}
        >
          Search
        </Button>
        <Button
          variant="outlined"
          onClick={handleDownload}
          disabled={isDownloading}
          sx={{
            height: 32,
            fontSize: "0.75rem",
            textTransform: "none",
            padding: "4px 10px",
          }}
        >
          {isDownloading ? "Downloading..." : "Download CSV"}
        </Button>
      </Box>

      {/* TABLE */}
      <Paper elevation={1} sx={{ overflowX: "auto" }}>
        <Table size="small" sx={{ minWidth: 650 }}>
          <TableHead>
            <TableRow sx={{ backgroundColor: "#0288d1" }}>
              <TableCell sx={{ color: "white", fontWeight: 600 }}>Date</TableCell>
              <TableCell sx={{ color: "white", fontWeight: 600 }}>Mobile</TableCell>
              <TableCell sx={{ color: "white", fontWeight: 600 }}>Short Code</TableCell>
              <TableCell sx={{ color: "white", fontWeight: 600 }}>Keyword</TableCell>
              <TableCell sx={{ color: "white", fontWeight: 600 }}>Received Text</TableCell>
              <TableCell sx={{ color: "white", fontWeight: 600 }}>Reply Msg</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                  <CircularProgress size={24} />
                </TableCell>
              </TableRow>
            ) : logs.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                  No Records Found
                </TableCell>
              </TableRow>
            ) : (
              logs.map((row) => (
                <TableRow key={row.logid} hover>
                  <TableCell>{dayjs(row.logdate).format("DD/MM/YYYY")}
                    <br />
                    {dayjs(row.logdate).format("HH:mm:ss")}
                  </TableCell>
                  <TableCell>{row.mobile}</TableCell>
                  <TableCell>{row.shortcode}</TableCell>
                  <TableCell>{row.keyword}</TableCell>
                  <TableCell>{row.received_text}</TableCell>
                  <TableCell>{row.response_sms_status}</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Paper>

      {/* Pagination */}
      <Box sx={{ mt: 2, display: "flex", alignItems: "center", gap: 1 }}>
        <Typography sx={{ fontSize: "0.8rem", color: "text.secondary" }}>
          Page {filters.page} of {totalPages}
        </Typography>
        <Pagination
          count={totalPages}
          page={filters.page}
          onChange={(_, page) => setFilters((prev) => ({ ...prev, page }))}
          size="small"
          color="primary"
        />
      </Box>
    </Box>
  );
};

export default KeywordReport;